import { useEffect } from 'react';
import { TagChip } from '@/components/TagChip';
import {
  describeDue, formatIstDayMonth, formatIstTime, formatIstWeekday, istDayKey, isWindowOpen,
  type Conversation,
} from '@wa/shared';
import { Avatar } from '@/components/Avatar';
import { Ticks } from '@/components/Ticks';

interface Props {
  conversations: Conversation[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  isLoading: boolean;
  search: string;
  onSearch: (value: string) => void;
  onOpenViews?: () => void;
  /** What the list is currently showing, and how many — see the header. */
  title: string;
  total?: number;
  /**
   * The view chips, rendered under this component's own header rather than above
   * it. Passed in rather than imported so the list does not have to know about
   * filters — but the *order* matters and only the list can enforce it: title,
   * then search, then the chips. An earlier arrangement put the chips at the very
   * top, above the title, and the panel read as four unrelated bands stacked up
   * before any content.
   */
  tabs?: React.ReactNode;
  onLoadMore?: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;

  /** The ids ticked right now. Owned by the page, because the bulk actions are. */
  picked: Set<number>;
  onPickedChange: (next: Set<number>) => void;
  /** The bulk action bar, rendered in this component's header when anything is
      ticked. Passed in for the same reason the view chips are: the list should
      not have to know what an agent can be assigned to. */
  actions?: React.ReactNode;
}

export function ConversationList({
  conversations, selectedId, onSelect, isLoading,
  search, onSearch, onOpenViews, onLoadMore, hasMore, isLoadingMore, title, total, tabs,
  picked, onPickedChange, actions,
}: Props) {
  /*
   * Selection mode is "is anything ticked", not a mode somebody switches on.
   *
   * The design being matched has a Select button that turns the avatars into
   * checkboxes, and it also lets you tick one straight from a hover. Two ways in
   * and one way out is a state machine; "the checkboxes are showing because
   * something is ticked" is not, and it means the way out is always the same —
   * untick the last one, or press Escape.
   */
  const picking = picked.size > 0;

  const toggle = (id: number) => {
    const next = new Set(picked);
    if (!next.delete(id)) next.add(id);
    onPickedChange(next);
  };

  const allShown = conversations.length > 0 && conversations.every((c) => picked.has(c.id));

  // Escape clears, from anywhere on the list. A selection with no visible way out
  // is how somebody ends up reloading the page to get their inbox back.
  useEffect(() => {
    if (!picking) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onPickedChange(new Set()); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [picking, onPickedChange]);

  return (
    <section className="flex min-h-0 flex-1 flex-col">
      <header className="flex shrink-0 flex-col gap-2 border-b border-(--color-line) px-3 py-2.5">
        <div className="flex items-center gap-2">
          {onOpenViews && (
            <button
              type="button"
              onClick={onOpenViews}
              className="tap grid shrink-0 place-items-center rounded-(--radius-field) px-1.5 text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)"
              aria-label="Open views"
            >
              <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                <path d="M3 5h14M3 10h14M3 15h9" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          )}

          {/* A title and a count. Both of DoubleTick's list panes lead with one,
              and it does more than decorate: "123 chats" is the only place the
              size of the queue is stated, and an agent reads it before anything. */}
          <h2 className="min-w-0 flex-1 truncate text-lead font-semibold tracking-tight">
            {title}
            {total !== undefined && (
              <span className="ml-2 text-small font-normal text-(--color-muted) tabular-nums">
                {total}
              </span>
            )}
          </h2>
        </div>

        {picking ? (
          /* The search box is replaced rather than crowded. At 300px a count, a
             select-all, a menu and a text field do not fit, and a search that
             cannot be typed into is worse than one that has stepped aside. */
          <div className="flex h-9 items-center gap-2">
            <button
              type="button"
              onClick={() => onPickedChange(allShown ? new Set() : new Set(conversations.map((c) => c.id)))}
              className="tap flex min-w-0 items-center gap-2 rounded-(--radius-field) px-1 py-1 text-left hover:bg-(--color-sunken)"
            >
              <Tick checked={allShown} />
              <span className="truncate text-small font-medium tabular-nums">
                {picked.size} selected
              </span>
            </button>

            <div className="ml-auto flex shrink-0 items-center gap-1">
              {actions}
              <button
                type="button"
                onClick={() => onPickedChange(new Set())}
                aria-label="Clear selection"
                className="tap grid size-7 place-items-center rounded-(--radius-field) text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)"
              >
                <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                  <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="relative min-w-0">
            <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true"
                 className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-(--color-faint)">
              <circle cx="9" cy="9" r="5.4" fill="none" stroke="currentColor" strokeWidth="1.6" />
              <path d="M13.2 13.2 17 17" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              value={search}
              onChange={(event) => onSearch(event.target.value)}
              placeholder="Search name or number"
              aria-label="Search conversations"
              className="tap h-9 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-sunken) pr-3 pl-8 text-body outline-none transition focus:border-(--color-brand) focus:bg-(--color-surface)"
            />
          </div>
        )}
      </header>

      {tabs}

      {/* Named, because a list with no name is announced as "list" and this
          screen has three of them. */}
      <ul aria-label="Conversations" className="pane-scroll min-h-0 flex-1">
        {isLoading && conversations.length === 0 && (
          <li className="px-4 py-8 text-center text-small text-(--color-muted)">Loading…</li>
        )}

        {!isLoading && conversations.length === 0 && (
          <li className="px-6 py-10 text-center text-small text-(--color-muted)">
            Nothing here. Messages to your business number appear in this list.
          </li>
        )}

        {conversations.map((conversation) => {
          const selected = conversation.id === selectedId;
          const outbound = conversation.lastMessageDirection === 1;
          // The response-target deadline, when the account measures one. Red once
          // it has passed — the same row the SLA breached view lists.
          const due = conversation.status === 2 ? null : describeDue(conversation.slaDueAt);
          // The same rule the Awaiting reply filter uses, and the reason the clock
          // is drawn: a customer has written and nobody has answered. Computed from
          // the row rather than fetched, because the row already carries both times.
          const awaiting = conversation.status !== 2
            && (conversation.lastOutboundAt === null
                || (conversation.lastInboundAt !== null
                    && conversation.lastInboundAt > conversation.lastOutboundAt));

          return (
            <li key={conversation.id} data-conversation={conversation.id} className="relative">
              <button
                type="button"
                onClick={() => (picking ? toggle(conversation.id) : onSelect(conversation.id))}
                aria-current={selected ? 'true' : undefined}
                className={[
                  'group',
                  // A fixed height, not padding. With padding, a tagged row was
                  // 90px and an untagged one 68px, and a list whose rows change
                  // height as you scroll reads as unsettled however nice each row
                  // looks on its own. 72px holds three lines comfortably.
                  // 84px, not 72: the channel line is a fourth line and a row that
                  // clips its own content is worse than a taller row. Still fixed,
                  // for the reason it was fixed before — a list whose rows change
                  // height as you scroll reads as unsettled however good each row is.
                  'relative flex h-[96px] w-full items-center gap-3 border-b border-(--color-line-dim) px-3 text-left transition-colors',
                  selected ? 'bg-(--color-brand-wash)' : 'hover:bg-(--color-sunken)',
                ].join(' ')}
              >
                {/* No leading bar. It was there because a 1.06:1 wash needed help;
                    aria-current carries the state for a screen reader and the wash
                    now has an edge of its own against a white row. */}

                {/* The avatar and the checkbox swap places — the design being
                    matched REPLACES the avatar rather than dimming it, and at
                    40px a scrim over a coloured circle reads as a broken image.

                    A <span>, not a nested <button>: the whole row is already a
                    button, and a button inside a button is invalid markup that
                    browsers resolve by silently dropping one of them. */}
                <span
                  onClick={(event) => { event.stopPropagation(); toggle(conversation.id); }}
                  className="relative grid size-10 shrink-0 place-items-center"
                >
                  <span className={[
                    'col-start-1 row-start-1',
                    picking ? 'hidden' : 'group-hover:hidden group-focus-visible:hidden',
                  ].join(' ')}>
                    <Avatar name={conversation.displayName} waId={conversation.waId} size={40} />

                    {/* The channel badge. Ours is WhatsApp on every row today and
                        will not always be, which is why it is a badge on the
                        avatar rather than a decoration in the header. */}
                    <span
                      aria-hidden="true"
                      className="absolute -right-0.5 -bottom-0.5 grid size-4 place-items-center rounded-full bg-(--color-surface)"
                    >
                    <WhatsAppMark />
                    </span>
                  </span>

                  <span className={[
                    'col-start-1 row-start-1 grid size-10 place-items-center rounded-full',
                    picking ? 'grid' : 'hidden group-hover:grid group-focus-visible:grid',
                  ].join(' ')}>
                    <Tick checked={picked.has(conversation.id)} />
                  </span>
                </span>

                {/* min-w-0 is what stops a long name or an unbroken number from
                    forcing this row wider than the column. */}
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  {/* Name on the left; the clock and the unread count on the
                      right. The stamp is NOT here — it sits on the preview line,
                      which is where the design being matched puts it, and the
                      reason is that the two things an agent scans a queue for are
                      the name and how many are waiting. A timestamp between them
                      is noise on the one line that has to be read fastest. */}
                  <span className="flex items-center gap-2">
                    <span className={[
                      // 16/24, 600 when unread and 500 when read — measured off
                      // the reference row, where the name is the one thing set a
                      // step above the rest of the list.
                      'min-w-0 flex-1 truncate text-lead',
                      conversation.unreadCount > 0 ? 'font-semibold' : 'font-medium',
                    ].join(' ')}>
                      {conversation.displayName ?? `+${conversation.waId}`}
                    </span>

                    {awaiting && (
                      <span
                        aria-label="Waiting on a reply"
                        title="Waiting on a reply"
                        className="shrink-0 text-(--color-bad)"
                      >
                        {/* A stopwatch, not an alarm clock. The first version had
                            two splayed bells on top, which at 14px reads as a
                            wind-up clock and not as "this one is on a timer". */}
                        <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
                          <circle cx="10" cy="11.4" r="5.8" fill="none" stroke="currentColor" strokeWidth="1.6" />
                          <path d="M10 8.4v3l2 1.2M8.3 2.9h3.4M10 2.9v2.6"
                                fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                      </span>
                    )}

                    {conversation.unreadCount > 0 && (
                      <span className="grid h-[18px] min-w-[18px] shrink-0 place-items-center rounded-(--radius-chip) bg-(--color-brand) px-1.5 text-micro font-bold text-(--color-brand-ink) tabular-nums">
                        {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
                      </span>
                    )}
                  </span>

                  {/* The number this came in on.
                      A tenant running one number never needed it; a tenant running
                      three cannot read the list without it, because every row looks
                      like every other row. */}
                  {conversation.channelName && (
                    <span className="flex items-center gap-1 text-small font-normal text-(--color-muted)">
                      <svg viewBox="0 0 20 20" width="12" height="12" aria-hidden="true" className="shrink-0">
                        <path
                          d="M10 8.6a1.4 1.4 0 1 0 0 2.8 1.4 1.4 0 0 0 0-2.8ZM7.1 6.6a4.4 4.4 0 0 0 0 6.8M12.9 6.6a4.4 4.4 0 0 1 0 6.8M5 4.4a7.4 7.4 0 0 0 0 11.2M15 4.4a7.4 7.4 0 0 1 0 11.2"
                          fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"
                        />
                      </svg>
                      <span className="min-w-0 truncate">{conversation.channelName}</span>
                    </span>
                  )}

                  <span className="flex items-center gap-1.5">
                    {/* `!= null` covers both, and both happen: absent on an older API
                        that does not return the field, and null on a row whose last
                        message came in — a tick is for a message we sent. */}
                    {outbound && conversation.lastMessageStatus != null && (
                      <span className="shrink-0"><Ticks status={conversation.lastMessageStatus} /></span>
                    )}
                    <span className={[
                      // 14/21 at weight 400 on the reference; unread is told by the
                      // name's weight and the badge, not by making the preview bold.
                      'min-w-0 flex-1 truncate text-body font-normal',
                      conversation.unreadCount > 0 ? 'text-(--color-ink)' : 'text-(--color-muted)',
                    ].join(' ')}>
                      {conversation.lastMessagePreview ?? 'No messages yet'}
                    </span>
                    <span className="shrink-0 text-micro font-semibold text-(--color-faint) tabular-nums">
                      {formatStamp(conversation.lastMessageAt)}
                    </span>
                  </span>

                  {/* One line, never two.
                      This row used to carry up to three tag chips, the assignee and
                      an EXPIRED badge, wrapping to a third line — which made a 36px
                      row simultaneously cramped and noisy, and is a large part of
                      why the list read as cluttered rather than dense. Now it is a
                      single clipped line: two tags, then the assignee, and the row
                      height cannot change whatever a conversation carries. */}
                  {(conversation.tags.length > 0 || conversation.assignedAgentName || !isWindowOpen(conversation) || due) && (
                    <span className="flex h-4 items-center gap-1.5 overflow-hidden">
                      {due && (
                        <span className={`shrink-0 rounded-(--radius-chip) px-1.5 py-px text-micro font-medium tabular-nums ${due.overdue ? 'bg-(--color-bad-wash) text-(--color-bad)' : 'bg-(--color-sunken) text-(--color-muted)'}`}>
                          {due.text}
                        </span>
                      )}
                      {!isWindowOpen(conversation) && (
                        <span className="shrink-0 rounded-(--radius-chip) bg-(--color-note) px-1.5 py-px text-micro font-medium text-(--color-note-strong)">
                          Expired
                        </span>
                      )}
                      {conversation.tags.slice(0, 2).map((tag) => (
                        <TagChip key={tag.id} tag={tag} size="xs" />
                      ))}
                      {conversation.tags.length > 2 && (
                        <span className="shrink-0 text-micro text-(--color-faint) tabular-nums">
                          +{conversation.tags.length - 2}
                        </span>
                      )}
                      {conversation.assignedAgentName && (
                        <span className="min-w-0 truncate text-micro text-(--color-faint)">
                          {conversation.assignedAgentName}
                        </span>
                      )}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}

        {hasMore && (
          <li className="p-3">
            <button
              type="button"
              onClick={onLoadMore}
              disabled={isLoadingMore}
              className="tap w-full rounded-md border border-(--color-line) py-2 text-small text-(--color-muted) hover:text-(--color-ink) disabled:opacity-50"
            >
              {isLoadingMore ? 'Loading…' : 'Load older conversations'}
            </button>
          </li>
        )}
      </ul>
    </section>
  );
}

/**
 * Today shows a clock, this week a weekday, older a date. Same rule as every chat
 * client — but every branch of it in IST.
 *
 * "Today" is an IST question, not a browser-zone one: on a laptop set to UTC a
 * message sent at 00:30 IST belongs to yesterday, and the row shows a weekday
 * where the thread beside it shows a clock.
 */
function formatStamp(iso: string): string {
  const date = new Date(iso);
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);

  if (istDayKey(iso) === istDayKey(new Date().toISOString())) return formatIstTime(iso);
  if (days < 7) return formatIstWeekday(iso);
  return formatIstDayMonth(iso);
}

/**
 * The channel badge.
 *
 * The real WhatsApp mark — the speech bubble with the handset inside it — not the
 * bare handset this used to draw. At thirteen pixels a handset alone reads as a
 * phone icon, which is what a call button looks like, and the whole job of this
 * badge is to say which network the conversation arrived on.
 *
 * #25D366 is WhatsApp's own green and is not a theme token on purpose: it is
 * somebody else's brand, it must not shift with our palette, and it must be the
 * same in dark mode.
 */
function WhatsAppMark() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
      <circle cx="12" cy="12" r="12" fill="#25D366" />
      {/* Three solid shapes, no thin rings. The first version drew the bubble as
          an outline the way the full logo does, and at thirteen pixels a
          one-pixel white ring is sub-pixel noise — it rendered as a green blob
          with speckles. A filled white bubble with the handset knocked back out
          of it in green is how the mark survives being this small. */}
      <path
        fill="#FFFFFF"
        d="M12 4.9a7.1 7.1 0 0 0-6 10.9l-1 3.6 3.7-1A7.1 7.1 0 1 0 12 4.9Z"
      />
      <path
        fill="#25D366"
        d="M9.98 8.86c-.14-.31-.28-.31-.41-.32h-.35c-.12 0-.32.05-.49.23-.17.18-.64.63-.64 1.53 0 .9.66 1.77.75 1.9.09.12 1.28 2.04 3.16 2.78 1.56.62 1.88.5 2.22.46.34-.03 1.09-.44 1.24-.87.15-.43.15-.8.1-.88-.04-.07-.16-.12-.34-.2-.18-.09-1.08-.53-1.25-.6-.17-.06-.29-.09-.41.09-.12.19-.47.6-.58.72-.1.12-.21.13-.39.05-.18-.09-.77-.29-1.46-.9-.54-.48-.9-1.08-1.01-1.26-.1-.18-.01-.28.08-.37.09-.09.19-.22.28-.32.09-.11.12-.19.19-.32.06-.13.03-.24-.02-.33-.05-.09-.41-1-.57-1.36Z"
      />
    </svg>
  );
}

/**
 * One checkbox, drawn rather than an <input>.
 *
 * It has to sit inside a row that is itself a button, where a real checkbox is
 * both invalid markup and a second tab stop on every row of a list that can be a
 * thousand long. The row carries the state for a screen reader through
 * aria-current and the count in the header; this is the picture.
 */
function Tick({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={[
        'grid size-[18px] shrink-0 place-items-center rounded border transition-colors',
        'motion-reduce:transition-none',
        checked
          ? 'border-(--color-brand) bg-(--color-brand) text-(--color-brand-ink)'
          : 'border-(--color-faint) bg-(--color-surface)',
      ].join(' ')}
    >
      {checked && (
        <svg viewBox="0 0 12 12" width="11" height="11">
          <path d="m2.5 6.2 2.3 2.3L9.5 3.8" fill="none" stroke="currentColor"
                strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );
}
